import { Handle, Position } from '@xyflow/react';
import type { Node, NodeProps } from '@xyflow/react';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import { EllipsisTooltip } from '../../components/EllipsisTooltip';
import { useColorToMuiColor } from '../../hooks/useColorToMuiColor.hook';
import { iconToMuiIcon } from '../../utils/icon-to-mui-icon.util';

import type { WorkflowNodeData } from './utils/build-workflow-graph.util';

type Props = NodeProps<Node<WorkflowNodeData>>;

export const WorkflowStateNode = ({ data }: Props) => {
  const { colorToMuiColor } = useColorToMuiColor();

  if (data.isStartMarker) {
    return (
      <Stack spacing={0.5} sx={{ alignItems: 'center' }}>
        <FiberManualRecordIcon color="action" />
        <Handle position={Position.Right} type="source" />
      </Stack>
    );
  }

  const backgroundColor = data.color ? colorToMuiColor(data.color) : undefined;

  return (
    <Paper
      elevation={2}
      sx={{
        alignItems: 'center',
        borderStyle: data.isEndPoint ? 'dashed' : 'solid',
        borderWidth: 2,
        borderColor: backgroundColor ?? 'divider',
        display: 'flex',
        gap: 1,
        minWidth: 180,
        px: 1.5,
        py: 1,
      }}
    >
      <Handle position={Position.Left} type="target" />
      {data.icon ? (
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: backgroundColor,
            borderRadius: '50%',
            color: 'common.white',
            display: 'flex',
            flexShrink: 0,
            height: 32,
            justifyContent: 'center',
            width: 32,
            '& svg': { fontSize: '1.1rem' },
          }}
        >
          {iconToMuiIcon(data.icon)}
        </Box>
      ) : null}
      <Stack sx={{ minWidth: 0 }}>
        <EllipsisTooltip title={data.label}>
          <Typography noWrap variant="subtitle2">
            {data.label}
          </Typography>
        </EllipsisTooltip>
        {data.stateCode ? (
          <EllipsisTooltip title={data.stateCode}>
            <Typography
              color="text.secondary"
              noWrap
              sx={{ fontFamily: 'monospace' }}
              variant="caption"
            >
              {data.stateCode}
            </Typography>
          </EllipsisTooltip>
        ) : null}
      </Stack>
      <Handle position={Position.Right} type="source" />
    </Paper>
  );
};
